import { useMemo, useState } from "react";
import { barterBadgeLabel, barterSplit, recurringBilling } from "@/lib/barter";
import { Link } from "react-router-dom";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBudgets, type Budget } from "@/hooks/useBudgets";
import { budgetLineSummary } from "@/lib/budgetSummary";
import { BudgetSignatureDialog } from "./BudgetSignatureDialog";
import { isProposalDocumentReady } from "@/lib/proposalDocument";
import { formatBRL } from "@/lib/format";

const statusLabel: Record<string, string> = {
  draft: "Rascunho",
  sent: "Enviado",
  partially_signed: "Parcialmente assinado",
  signed: "Assinado",
  accepted: "Aceito",
  rejected: "Rejeitado",
};

const fmt = (v: number) => formatBRL(v);

/**
 * Right pane of the Cockpit dashboard widget — a live preview of the most
 * recently created draft proposal, built from the same primitives
 * LeadDetail's Orçamento step uses (`calculateBudgetTotals`, `useBudgets`,
 * `BudgetSignatureDialog`) so "Enviar para assinatura" triggers the real
 * send-budget-signature flow, not a stub.
 */
export function BudgetComposerPreview() {
  const { data: draftBudgets, isLoading } = useBudgets({ status: "draft" });
  const [signatureBudget, setSignatureBudget] = useState<Budget | null>(null);

  // Most recently created draft across all leads — the one most likely to
  // still need review/sending. No "current lead" concept exists on this
  // dashboard, so this is the closest honest proxy for "what's open now".
  const budget = draftBudgets?.[0] ?? null;

  // Stored totals are the source of truth (the editor derives them from the
  // items); recomputing here used to pass platform_fee (a 0–1 rate) as a
  // percent and drift for "other" platforms.
  const totals = useMemo(() => {
    if (!budget) return null;
    return {
      gross: budget.gross_total,
      fee: budget.gross_total - budget.net_total,
      net: budget.net_total,
    };
  }, [budget]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <Loader2 className="h-5 w-5 animate-spin text-panel-foreground/60" />
      </div>
    );
  }

  if (!budget || !totals) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-gradient-to-b from-primary/15 to-transparent p-10 text-center">
        <p className="text-sm font-medium text-panel-foreground">Nenhuma proposta em rascunho</p>
        <p className="max-w-[280px] text-xs text-panel-foreground/60">
          Quando um orçamento em rascunho existir, ele aparece aqui pronto para revisar e enviar
          para assinatura.
        </p>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="border-panel-foreground/20 bg-transparent text-panel-foreground hover:bg-panel-foreground/10 hover:text-panel-foreground"
        >
          <Link to="/admin/pipeline">Ver leads</Link>
        </Button>
      </div>
    );
  }

  const daysAgo = Math.max(
    0,
    Math.floor((Date.now() - new Date(budget.created_at).getTime()) / 86_400_000),
  );

  return (
    <div className="flex flex-1 flex-col gap-5 bg-gradient-to-b from-primary/15 to-transparent p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-panel-foreground/55">
            Proposta em aberto
          </div>
          <div className="mt-[7px] flex flex-wrap items-center gap-[11px]">
            <span className="text-2xl font-bold tracking-[-0.02em] text-panel-foreground">
              {budget.lead?.name ?? budget.client_project?.title ?? budget.title}
            </span>
            <span className="inline-flex h-6 items-center rounded-full bg-panel-foreground px-2.5 text-[11px] font-bold text-primary-deep">
              {statusLabel[budget.status] ?? budget.status}
            </span>
          </div>
          <div className="mt-[5px] text-[12.5px] text-panel-foreground/60">
            PROP-{budget.id.slice(0, 8).toUpperCase()} · criada{" "}
            {daysAgo === 0 ? "hoje" : `há ${daysAgo} dia${daysAgo === 1 ? "" : "s"}`}
            {budget.lead?.name ? ` · ${budget.lead.name}` : ""}
            {budget.lead?.email ? `, ${budget.lead.email}` : ""}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11.5px] text-panel-foreground/55">Valor total</div>
          <div className="text-2xl font-bold tracking-[-0.02em] text-panel-foreground">
            {fmt(totals.net)}
          </div>
          {budget.recurring_monthly_total > 0 && (
            <div className="mt-1 text-[12px] font-semibold text-panel-foreground/75">
              {recurringBilling(budget).inBarter
                ? `${fmt(budget.recurring_monthly_total)}/mês na permuta`
                : `+ ${fmt(budget.recurring_monthly_total)}/mês`}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-[13px] border border-panel-foreground/10 bg-panel-foreground/[0.08] p-3.5">
          <div className="text-base font-bold text-panel-foreground">{fmt(totals.gross)}</div>
          <div className="mt-1 text-[11px] text-panel-foreground/60">Bruto</div>
        </div>
        <div className="rounded-[13px] border border-panel-foreground/10 bg-panel-foreground/[0.08] p-3.5">
          <div className="text-base font-bold text-panel-foreground">{fmt(totals.fee)}</div>
          <div className="mt-1 text-[11px] text-panel-foreground/60">
            Taxa ({budget.platform ?? "direct"})
          </div>
        </div>
        <div className="rounded-[13px] border border-panel-foreground/10 bg-panel-foreground/[0.08] p-3.5">
          <div className="text-base font-bold text-panel-foreground">{fmt(totals.net)}</div>
          <div className="mt-1 text-[11px] text-panel-foreground/60">
            Líquido
            {(budget.barter_total ?? 0) > 0 && (
              <>
                {" "}
                · a cobrar {fmt(barterSplit(budget.net_total, budget.barter_percent).cashTotal)} (
                {barterBadgeLabel(budget.barter_percent)})
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-panel-foreground/10 pt-4">
        <div className="text-xs text-panel-foreground/60">{budgetLineSummary(budget.items)}</div>
        <div className="flex gap-[9px]">
          <Button
            asChild
            variant="outline"
            className="h-[38px] rounded-[10px] border-panel-foreground/20 bg-transparent px-[15px] text-[12.5px] font-semibold text-panel-foreground hover:bg-panel-foreground/10 hover:text-panel-foreground"
          >
            <Link
              to={
                budget.lead_id ? `/admin/leads/${budget.lead_id}?tab=proposta` : "/admin/pipeline"
              }
            >
              Pré-visualizar
            </Link>
          </Button>
          <Button
            className="h-[38px] rounded-[10px] bg-panel-foreground px-[18px] text-[12.5px] font-bold text-primary-deep hover:bg-panel-foreground/90"
            onClick={() => setSignatureBudget(budget)}
            disabled={!isProposalDocumentReady(budget)}
            title={
              isProposalDocumentReady(budget)
                ? undefined
                : "Preencha o documento da proposta na página do lead"
            }
          >
            <Send className="mr-1.5 h-3.5 w-3.5" /> Enviar para assinatura
          </Button>
        </div>
      </div>

      <BudgetSignatureDialog
        budget={signatureBudget}
        open={!!signatureBudget}
        onOpenChange={(v) => {
          if (!v) setSignatureBudget(null);
        }}
      />
    </div>
  );
}
